import React, { useEffect } from 'react';
import {
  Card,
  Table,
  Form,
  Select,
  Checkbox,
  Input,
  Button,
  Space,
  Typography,
  Modal,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ButtonBlack from '../../../components/ui/buttons/ButtonBlack';
import IconDownloadJson from '../../ui/icons/IconDownloadJson';
import { Engine } from '../../../api/types';
import { useData } from "./useData";
import Roles from '../../../roles/Roles';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;
const { TextArea } = Input;

interface FactorChannelsProps {
  engine: Engine;
}

interface TableRow {
  key: string;
  channelIndex: number;
  factorIndex: number;
  isFirstInChannel: boolean;
  factorsCount: number;
}

export default function FactorChannels({ engine }: FactorChannelsProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const {
    metricsOptions,
    channelsOptions,
    channels,
    isValuesChanged,
    isFactorsSaving,
    isChannelModalVisible,
    selectedChannelIri,
    setSelectedChannelIri,
    moveFactor,
    handleChannelSelectOk,
    handleChannelSelectCancel,
    onSave,
    downloadJson,
    setIsValuesChanged,
  } = useData(engine);

  useEffect(() => {
    form.setFieldsValue({ channels });
  }, [channels, form]);

  const tableData: TableRow[] = channels.flatMap((channel, channelIndex) =>
    (channel.factors || []).map((_, factorIndex) => ({
      key: `${channelIndex}-${factorIndex}`,
      channelIndex,
      factorIndex,
      isFirstInChannel: factorIndex === 0,
      factorsCount: channel.factors.length,
    }))
  );

  const columns: ColumnsType<TableRow> = [
    {
      title: t('factors.channelName'),
      width: '15%',
      render: (_, record) =>
        record.isFirstInChannel ? (
          <div data-row-key={record.key} style={{ padding: '8px 12px' }}>
            {channels[record.channelIndex]?.name || t('factors.nonChannel')}
          </div>
        ) : null,
      onCell: record => ({
        rowSpan: record.isFirstInChannel ? record.factorsCount : 0,
      }),
    },
    {
      title: t('factors.factorName'),
      width: '18%',
      render: (_, record) => (
        <div style={{ padding: '8px 12px' }}>
          {channels[record.channelIndex]?.factors[record.factorIndex]?.name}
        </div>
      ),
    },
    {
      title: t('factors.factorDescription'),
      width: '20%',
      render: (_, record) => (
        <Form.Item
          name={['channels', record.channelIndex, 'factors', record.factorIndex, 'description']}
          noStyle
        >
          <TextArea
            variant="borderless"
            autoSize
            placeholder={t('common.write')}
            disabled={!Roles.hasAccess('canManageFactors')}
          />
        </Form.Item>
      ),
    },
    {
      title: t('factors.mediaType'),
      width: '60px',
      align: 'center',
      render: (_, record) => {
        const factor = channels[record.channelIndex]?.factors[record.factorIndex];
        return (
          <Checkbox
            checked={factor?.isChannel}
            disabled={!Roles.hasAccess('canManageFactors')}
            onChange={e => moveFactor(record.channelIndex, record.factorIndex, e.target.checked)}
          />
        );
      },
    },
    {
      title: t('factors.metric'),
      width: '22%',
      render: (_, record) => (
        <Form.Item
          name={['channels', record.channelIndex, 'factors', record.factorIndex, 'metric']}
          style={{ width: '100%', margin: 0 }}
        >
          <Select
            variant="borderless"
            placeholder={t('common.choose')}
            showSearch
            allowClear
            labelInValue
            optionFilterProp="label"
            options={metricsOptions}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 240 }}
            disabled={!Roles.hasAccess('canManageFactors')}
          />
        </Form.Item>
      ),
    },
    {
      title: t('factors.managerText'),
      width: '18%',
      render: (_, record) => (
        <Form.Item
          name={['channels', record.channelIndex, 'factors', record.factorIndex, 'managerText']}
          noStyle
        >
          <TextArea
            variant="borderless"
            autoSize
            placeholder={t('common.write')}
            disabled={!Roles.hasAccess('canManageFactors')}
          />
        </Form.Item>
      ),
    },
    {
      title: t('factors.audienceText'),
      width: '15%',
      render: (_, record) =>
        record.isFirstInChannel ? (
          <Form.Item name={['channels', record.channelIndex, 'audienceText']} noStyle>
            <TextArea
              variant="borderless"
              autoSize
              placeholder={t('common.write')}
              disabled={!Roles.hasAccess('canManageFactors')}
            />
          </Form.Item>
        ) : null,
      onCell: record => ({
        rowSpan: record.isFirstInChannel ? record.factorsCount : 0,
      }),
    },
    {
      title: t('factors.useInPlanner'),
      width: '100px',
      align: 'center',
      render: (_, record) => (
        <Form.Item
          name={['channels', record.channelIndex, 'factors', record.factorIndex, 'isActive']}
          valuePropName="checked"
          noStyle
        >
          <Checkbox disabled={!Roles.hasAccess('canManageFactors')} />
        </Form.Item>
      ),
    },
  ];

  return (
    <Card style={{ paddingBottom: 40 }}>
      <Form
        form={form}
        onFinish={onSave}
        onValuesChange={() => setIsValuesChanged(true)}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={5}>{t('factors.title')}</Title>
            {Roles.hasAccess('canManageFactors') && (
              <ButtonBlack
                onClick={() => form.submit()}
                disabled={!isValuesChanged}
                loading={isFactorsSaving}
              >
                {t('common.save')}
              </ButtonBlack>
            )}
          </div>
          {engine?.json && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
              <Space>
                <IconDownloadJson />
                <span>file.json</span>
              </Space>
              <Button variant="filled" color="default" onClick={downloadJson}>
                {t('common.download')}
              </Button>
            </div>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          bordered
          size="middle"
          rowKey="key"
          sticky={{ offsetHeader: 48 }}
        />
      </Form>

      <Modal
        title={t('common.choose')}
        open={isChannelModalVisible}
        onOk={handleChannelSelectOk}
        onCancel={handleChannelSelectCancel}
        okText={t('common.yes')}
        cancelText={t('common.cancel')}
      >
        <Select
          style={{ width: '100%', marginTop: 16 }}
          placeholder={t('common.choose')}
          showSearch
          optionFilterProp="label"
          value={selectedChannelIri}
          onChange={setSelectedChannelIri}
          options={channelsOptions}
        />
      </Modal>
    </Card>
  );
}
